import type { PortalNotification } from '@/modules/notifications/api'
import {
  QueueIcon,
  queueIconColor,
  queueIconMeaningForNotification,
  type QueueIconMeaning,
} from '@/components/QueueIcon'
import type { NeedsYouItem } from './useNeedsYou'

export function needsYouIconMeaning(item: NeedsYouItem): QueueIconMeaning {
  switch (item.kind) {
    case 'conversation':
      return 'customer'
    case 'relay':
    case 'handoff':
      return 'notification'
    case 'invoice_overdue':
    case 'invoice_review':
      return 'invoice'
    // The derived "Finish your business profile" task (#486) reads as a
    // reminder: it is a to-do the owner works through, not an event. Listed
    // first so `case 'reminder': return 'reminder'` stays adjacent — the
    // contracts queue-icon pin reads this file as source.
    case 'profile_gap':
    case 'reminder':
      return 'reminder'
    case 'notification':
      return queueIconMeaningForNotification(
        item.notification?.entityType,
        item.notification?.eventType,
      )
  }
}

export function needsYouIconSeed(item: NeedsYouItem): string {
  if (item.kind === 'conversation') {
    return item.conversation?.customerId ?? item.conversationId ?? item.id
  }
  return item.id
}

type HomeClayIconProps =
  | {
      item: NeedsYouItem
      meaning?: never
      seed?: never
      className?: string
    }
  | {
      item?: never
      meaning: QueueIconMeaning
      seed: string
      className?: string
    }

/** Meaning-correct Home artwork with a stable selection from all eight colors. */
export function HomeClayIcon(props: HomeClayIconProps) {
  const meaning = props.item ? needsYouIconMeaning(props.item) : props.meaning
  const seed = props.item ? needsYouIconSeed(props.item) : props.seed
  return <QueueIcon meaning={meaning} color={queueIconColor(seed)} className={props.className} />
}

export function HomeNotificationIcon({
  notification,
  className,
}: {
  notification: PortalNotification
  className?: string
}) {
  return (
    <HomeClayIcon
      meaning={queueIconMeaningForNotification(notification.entityType, notification.eventType)}
      seed={notification.id}
      className={className}
    />
  )
}
